import { Injectable } from '@angular/core';
import { Task } from 'src/core/master-data/domain/task';
import { TaskRepository } from 'src/core/tasks/domain/task-repository';
import { GarbageCollectorService } from 'src/core/garbage-collector/domain/garbage-collector.service';
import { TaskService } from 'src/core/tasks/domain/task.service';
import { RejectTaskRequest } from './reject-task-request';
import { QueueService } from 'src/core/queue/domain/queue.service';
import { RejectTaskQueueElement } from 'src/core/queue/domain/elements/reject-task-queue-element';
import { QueueElement, QueueElementError } from 'src/core/queue/domain/queue-element';
import { MasterDataService } from 'src/core/master-data/domain/master-data-service';
import { ConnectionException } from 'src/core/common/domain/exceptions/connection-exception';
import { UnauthorizedException } from 'src/core/authentication/domain/exceptions/unauthorized-exception';

@Injectable({
  providedIn: 'any',
})
export class RejectTask {
  private isSavedToServer = false;
  private isRejectedToServer = false;
  private isRetry = false;
  private task: Task;

  constructor(
    private readonly taskRepository: TaskRepository,
    private readonly garbageCollector: GarbageCollectorService,
    private readonly taskService: TaskService,
    private readonly queueService: QueueService,
    private readonly masterDataService: MasterDataService
  ) {}

  async execute(request: RejectTaskRequest, bulkMode: boolean = false): Promise<void> {
    this.task = request.task;
    const queueElement = request.queueElement;
    this.isRetry = queueElement !== null;
    this.isSavedToServer = queueElement !== null ? queueElement.operationStatus.isSavedToServer : false;
    this.isRejectedToServer = queueElement !== null ? queueElement.operationStatus.isRejectedToServer : false;

    try {
      if (!this.isRetry) {
        this.task.reject();
        this.markTaskAsFinalDependingOnResolution();
        await this.saveTaskIntoLocalDevice();
      }

      if (bulkMode) {
        await this.enqueueOperation();
        return;
      }

      const taskIsInQueue = this.queueService.hasQueueElementWithTaskId(this.task.id);
      if (taskIsInQueue && !this.isRetry) {
        await this.enqueueOperation();
        return;
      }

      await this.saveTaskToExternalServer(this.task);
      await this.rejectTaskToExternalServer(this.task);
      await this.garbageCollector.addDirectoryToDelete(this.taskService.getAttachmentsDirectoryForTaskId(this.task.id));
    } catch (exception) {
      if (exception instanceof ConnectionException) {
        if (this.isRetry) {
          await this.keepQueueElementForNextRetry(queueElement);
          throw exception;
        }

        await this.enqueueOperation();
        return;
      }

      // exceptions during retrying do not enqueue or rollback the task, but store error to display in list
      if (this.isRetry) {
        await this.storeErrorsInQueueElement(exception, queueElement);
        throw exception; // propagate exception without doing anything else
      }

      // otherwise, rollback
      this.task.rollback();
      await this.saveTaskIntoLocalDevice();
      throw exception; // propagate exception
    }
  }

  private async enqueueOperation(errors: Array<QueueElementError> = []) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isRejectedToServer: this.isRejectedToServer,
    };
    await this.queueService.enqueue(RejectTaskQueueElement.fromTask(this.task, operationStatus, errors));
  }

  private async saveTaskIntoLocalDevice() {
    await this.taskService.saveTask(this.task);
  }

  private async saveTaskToExternalServer(task: Task) {
    if (this.isSavedToServer) {
      return;
    }
    await this.taskRepository.save(task);
    this.isSavedToServer = true;
  }

  private async rejectTaskToExternalServer(task: Task) {
    if (this.isRejectedToServer) {
      return;
    }
    await this.taskRepository.reject(task);
    this.isRejectedToServer = true;
  }

  /**
   * A retried element is still in the queue, so enqueueing it again would be rejected as a duplicate,
   * and the caller would then drop the original as processed. Throwing keeps it there instead, with the
   * progress made so far, so the next retry does not repeat the steps that already reached the server.
   */
  private async keepQueueElementForNextRetry(queueElement: RejectTaskQueueElement) {
    await this.updateQueueElementWithErrors(queueElement, queueElement.errors);
  }

  private async storeErrorsInQueueElement(exception: unknown, queueElement: RejectTaskQueueElement) {
    // an expired session is handled by the session flow, it says nothing about this operation
    if (exception instanceof UnauthorizedException) {
      return;
    }
    await this.updateQueueElementWithErrors(queueElement, QueueElement.errorsFromException(exception));
  }

  private async updateQueueElementWithErrors(queueElement: RejectTaskQueueElement, errors: Array<QueueElementError>) {
    const operationStatus = {
      isSavedToServer: this.isSavedToServer,
      isRejectedToServer: this.isRejectedToServer,
    };
    queueElement.update(this.task, operationStatus, errors);
    await this.queueService.updateQueueElement(queueElement);
  }

  private markTaskAsFinalDependingOnResolution(): void {
    const taskResolutionId = this.task.getRejectTaskData().resolutions;
    if (!taskResolutionId) {
      return;
    }

    if (!this.masterDataService.getRejectTaskActions().isResolutionFinal(taskResolutionId)) {
      return;
    }

    this.task.markAsFinalState();
  }
}
